import React, { useRef, useEffect } from "react";

export type FormResultVariant = "success" | "info" | "warning";

export interface FormResultAction {
	label: string;
	onClick: () => void;
	variant?: "primary" | "secondary";
}

export interface FormResultProps {
	variant?: FormResultVariant;
	title: string | React.ReactNode;
	descriptions: (string | React.ReactNode)[];
	actions?: FormResultAction[];
}

const icons: Record<FormResultVariant, React.ReactNode> = {
	success: (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			viewBox="0 0 24 24"
			fill="currentColor"
			width="48"
			height="48"
		>
			<path
				fillRule="evenodd"
				d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm13.36-1.814a.75.75 0 10-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 001.14-.094l3.75-5.25z"
				clipRule="evenodd"
			/>
		</svg>
	),
	info: (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			viewBox="0 0 24 24"
			fill="currentColor"
			width="48"
			height="48"
		>
			<path
				fillRule="evenodd"
				d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm8.706-1.442c1.146-.573 2.437.463 2.126 1.706l-.709 2.836.042-.02a.75.75 0 01.67 1.34l-.04.022c-1.147.573-2.438-.463-2.127-1.706l.71-2.836-.042.02a.75.75 0 11-.671-1.34l.041-.022zM12 9a.75.75 0 100-1.5.75.75 0 000 1.5z"
				clipRule="evenodd"
			/>
		</svg>
	),
	warning: (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			viewBox="0 0 24 24"
			fill="currentColor"
			width="48"
			height="48"
		>
			<path
				fillRule="evenodd"
				d="M9.401 3.003c1.155-2 4.043-2 5.197 0l7.355 12.748c1.154 2-.29 4.5-2.599 4.5H4.645c-2.309 0-3.752-2.5-2.598-4.5L9.4 3.003zM12 8.25a.75.75 0 01.75.75v3.75a.75.75 0 01-1.5 0V9a.75.75 0 01.75-.75zm0 8.25a.75.75 0 100-1.5.75.75 0 000 1.5z"
				clipRule="evenodd"
			/>
		</svg>
	),
};

export default function FormResult({
	variant = "success",
	title,
	descriptions,
	actions,
}: FormResultProps) {
	const resultRef = useRef<HTMLDivElement>(null);

	// Scroll vers le résultat quand il apparaît
	useEffect(() => {
		if (resultRef.current) {
			resultRef.current.scrollIntoView({
				behavior: "smooth",
				block: "center",
			});
		}
	}, []);

	return (
		<div
			ref={resultRef}
			className={`gext-form__result gext-form__result--${variant}`}
		>
			<div className="gext-form__result-icon">{icons[variant]}</div>
			<h3 className="gext-form__result-title">{title}</h3>
			{descriptions.map((description, index) => (
				<p key={index} className="gext-form__result-description">
					{description}
				</p>
			))}
			{actions && actions.length > 0 && (
				<div className="gext-form__result-actions">
					{actions.map((action, index) => (
						<button
							key={index}
							type="button"
							className={`gext-form__cta-button gext-form__cta-button--${
								action.variant || "primary"
							}`}
							onClick={action.onClick}
						>
							{action.label}
						</button>
					))}
				</div>
			)}
		</div>
	);
}
